import {FC, ReactNode, useContext, useState, useEffect, Fragment} from 'react'
import {useOutletContext, useParams} from "react-router-dom";
import {useListingContext} from "../Hooks/useListingContext";
import Listing, {CustomAction} from "Components/Listing/Listing";
import {useTranslation} from "react-i18next";
import {Button, Modal} from "react-bootstrap";
import ListingSetting from "../Entity/Collection/ListingSetting";
import FormEmptyOffCanvas from "../Components/Modal/FormEmptyOffCanvas";
import DeleteModal from "../Components/Modal/DeleteModal";
import LoadingButton from "../Components/Button/LoadingButton";
import Api from "../Vendor/Api";
import EntityInterface from "../Vendor/Definition/EntityInterface";
import User, {PlatformUserPropertyValue} from "../Entity/Platform/User";
import Platform from "../Entity/Platform";
import Property from "../Entity/Property";
import {CollectionContext} from "../Vendor/Context/CollectionContextProvider";
import useTranslationDataUtils from "../Vendor/Utils/TranslationDataUtils";
import {UserContext} from "../Vendor/Context/UserContextProvider";

export interface UserProps {
}

const PlatformUserPage: FC<UserProps> = () => {
    const {t, i18n} = useTranslation()
    const {translate} = useTranslationDataUtils()
    const {getValue} = useContext(CollectionContext)
    const {currentUser} = useContext(UserContext)
    const [toggleReload, setToggleReload] = useState<string>('')
    const [user, setUser] = useState<User>({} as User)
    const [showDetails, setShowDetails] = useState<boolean>(false)
    const [activationLoading, setActivationLoading] = useState<{id?: number, action: 'enable'|'disable'}|undefined>(undefined)
    const params = useParams()
    const { items } = useListingContext()

    const [platform, show, setShow, search, setSearch] = useOutletContext<[Platform,boolean, (show:boolean) => void, string, (search:string) => void, (handleAdd: (() => void)|undefined) => void]>()

    const [setting] = useState<ListingSetting>({
        cardCondensedByDefault: false,
        cardShowBadge: false,
        cardShowStatus: false,
        cardShowSchedule: true,
        cardShowSizeAndWeight: null,
        filters: [],
        groupedActions: [],
        kanbanDatas: [],
        cardDatas: [],
        searchable: [],
        searchableByEmail: false,
        searchableByFirstName: false,
        searchableByLastName: false,
        searchableByName: false,
        sorters: [
        ],
        tableColumns: [
            {
                columnType: "identifier",
                visibleByDefault: true,
            },
            {
                column: 'email',
                visibleByDefault: true,
                render: (user: User) => (
                    <>
                        {UserEmail({ user: user })}
                        {!user.enabled && (
                            <span className="badge bg-info ms-1">
                                {t('app.waiting_validation')}
                            </span>
                        )}
                    </>
                ),
            },
            {
                column: 'name',
                visibleByDefault: true,
            },
            {
                column: 'currency',
                visibleByDefault: true,
            }
        ],
        views: ['table']
    })


    const UserEmail = ({ user }: { user: User }) => {
        return user.email
    }

    const handleClose = (reload = false) => {
        setShow(false)
        if (reload) {
            setToggleReload(new Date().toISOString())
        }
    }

    const handleShow = () => setShow(true)
    // remove
    const [showDelete, setShowDelete] = useState<boolean>(false)
    const handleDeleteClose = (reload = false) => {
        setUser({} as User)
        setShowDelete(false)
        if (reload) {
            setToggleReload(new Date().toISOString())
        }
    }

    const handleDeleteShow = () => setShowDelete(true)

    const onSuccess = (user: User) => {
        setUser(user)
        setShow(false)
        setToggleReload(new Date().toISOString())
    }

    const onExited = () => {
        setUser({} as User)
    }

    const editUser = (user: EntityInterface) => {
        setUser(user as User)
        handleShow()
    }

    const deleteUser = (user: EntityInterface) => {
        setUser(user as User)
        handleDeleteShow()
    }

    const showUserDetails = (user: EntityInterface) => {
        setUser(user as User)
        setShowDetails(true)
    }

    const handleDetailsClose = () => {
        setUser({} as User)
        setShowDetails(false)
    }

    const updateSelectedUser = (selectedUser: User) => {
        setUser((currentUser) => {
            if (currentUser.id !== selectedUser.id) {
                return currentUser
            }

            return {
                ...currentUser,
                ...selectedUser,
            }
        })
    }

    useEffect(() => {
        if (params.hasOwnProperty('id')) {
            const id = parseInt(params.id ?? '0', 10);
            if (id > 0) {
                const foundUser = Object.values(items).find(i => i.id === id) as User;
                if (foundUser) {
                    setUser(foundUser);
                    setShowDetails(true);
                }
            }
        }
    }, [params, items]);

    const isActivationLoading = (user: EntityInterface, action: 'enable'|'disable'): boolean => {
        if (!activationLoading) {
            return false
        }

        return activationLoading.id === user.id && activationLoading.action === action
    }

    const toggleUserActivation = (user: EntityInterface, enabled: boolean) => {
        if (!user.id) {
            return
        }

        const action = enabled ? 'enable' : 'disable'
        setActivationLoading({id: user.id, action})

        Api.patch(`/platform-setting/${platform?.id ?? 1}/user/${user.id}/${action}`)
            .then((response) => updateSelectedUser({
                ...user,
                ...(response.data ?? {}),
                enabled,
            } as User))
            .catch(() => {})
            .then(() => setToggleReload(new Date().toISOString()))
            .finally(() => setActivationLoading(undefined))
    }

    const handleEnableUser = (user: EntityInterface) => {
        if (!user.enabled) {
            toggleUserActivation(user, true)
        }
    }

    const handleDisableUser = (user: EntityInterface) => {
        toggleUserActivation(user, false)
    }

    const getProperty = (propertyValue: PlatformUserPropertyValue): Property|undefined => {
        if (propertyValue.property) {
            return propertyValue.property
        }

        if (
            propertyValue.value
            && typeof propertyValue.value === 'object'
            && 'property' in propertyValue.value
        ) {
            return (propertyValue.value as {property?: Property}).property
        }

        return undefined
    }

    const getPropertyData = (propertyValue: PlatformUserPropertyValue): unknown => {
        if (
            propertyValue.value
            && typeof propertyValue.value === 'object'
            && 'data' in propertyValue.value
        ) {
            return (propertyValue.value as {data?: unknown}).data
        }

        if ('data' in propertyValue) {
            return propertyValue.data
        }

        return propertyValue.value
    }

    const isEmptyPropertyData = (data: unknown): boolean => {
        if (data === null || data === undefined) {
            return true
        }

        if (typeof data === 'string') {
            return data.trim() === ''
        }

        if (Array.isArray(data)) {
            return data.length === 0
        }

        return false
    }

    const normalizeSlug = (slug?: string): string|undefined => {
        if (!slug) {
            return undefined
        }

        return slug.replace(/[-_ ]+([a-zA-Z0-9])/g, (_, letter: string) => letter.toUpperCase())
    }

    const findContactProperty = (key: string): Property|undefined => {
        return currentUser?.currentOrganization?.contactProperties?.find((property) => {
            return property.slug === key || normalizeSlug(property.slug) === key
        })
    }

    const normalizePropertyValueContainer = (container: unknown): PlatformUserPropertyValue[] => {
        if (!container) {
            return []
        }

        if (Array.isArray(container)) {
            return container as PlatformUserPropertyValue[]
        }

        if (typeof container === 'object') {
            return Object.entries(container as EntityInterface)
                .map(([key, value]) => ({
                    id: findContactProperty(key)?.id,
                    key,
                    property: findContactProperty(key),
                    value,
                } as PlatformUserPropertyValue))
        }

        return []
    }

    const getContactPropertyValues = (selectedUser: User): PlatformUserPropertyValue[] => {
        const contact = selectedUser.contact

        if (!contact) {
            return []
        }

        const propertyValues: PlatformUserPropertyValue[] = [
            ...normalizePropertyValueContainer(contact.properties),
            ...normalizePropertyValueContainer(contact.propertyValues),
            ...normalizePropertyValueContainer(contact.values),
        ]

        currentUser?.currentOrganization?.contactProperties?.forEach((property) => {
            const propertyKey = normalizeSlug(property.slug) ?? property.slug
            const value = contact[propertyKey] ?? contact[property.slug]

            propertyValues.push({
                id: property.id,
                property,
                value,
            } as PlatformUserPropertyValue)
        })

        const uniqueValues: PlatformUserPropertyValue[] = []
        const existingKeys: string[] = []

        propertyValues.forEach((propertyValue, index) => {
            const property = getProperty(propertyValue)
            const data = getPropertyData(propertyValue)

            const key = `${property?.id ?? propertyValue.key ?? index}`
            const existingIndex = existingKeys.indexOf(key)

            if (existingIndex === -1) {
                existingKeys.push(key)
                uniqueValues.push(propertyValue)
            } else {
                const existingValue = uniqueValues[existingIndex]
                if (isEmptyPropertyData(getPropertyData(existingValue)) && !isEmptyPropertyData(data)) {
                    uniqueValues[existingIndex] = propertyValue
                }
            }
        })

        return uniqueValues
    }

    const stringifyValue = (value: unknown): string => {
        if (value === null || value === undefined || value === '') {
            return t('app.empty')
        }

        if (typeof value === 'boolean') {
            return t(`app.${value ? 'yes' : 'no'}`)
        }

        if (typeof value === 'number' || typeof value === 'string') {
            return `${value}`
        }

        if (Array.isArray(value)) {
            return value.length ? value.map(stringifyValue).join(', ') : t('app.empty')
        }

        if (typeof value === 'object') {
            const objectValue = value as EntityInterface
            const translatedLabel = translate(objectValue, 'label') ?? translate(objectValue, 'name')

            if (translatedLabel) {
                return translatedLabel
            }

            if (objectValue.firstName || objectValue.lastName) {
                return `${objectValue.firstName ?? ''} ${objectValue.lastName ?? ''}`.trim()
            }

            if (objectValue.name || objectValue.label || objectValue.value) {
                return stringifyValue(objectValue.name ?? objectValue.label ?? objectValue.value)
            }

            const fields = Object.entries(objectValue)
                .filter(([, fieldValue]) => ['boolean', 'number', 'string'].includes(typeof fieldValue))
                .map(([fieldName, fieldValue]) => `${fieldName}: ${stringifyValue(fieldValue)}`)

            return fields.length ? fields.join(', ') : JSON.stringify(value)
        }

        return `${value}`
    }

    const formatCurrencyValue = (value: EntityInterface): string => {
        const amount = Number(value.amount ?? value.value ?? 0)
        const currency = value.currency ?? 'EUR'

        if (Number.isNaN(amount)) {
            return stringifyValue(value)
        }

        return new Intl.NumberFormat(i18n.language, {style: 'currency', currency}).format(amount / 100)
    }

    const formatDateValue = (value: unknown): string => {
        if (!value) {
            return t('app.empty')
        }

        const date = new Date(`${value}`)

        if (Number.isNaN(date.getTime())) {
            return stringifyValue(value)
        }

        return date.toLocaleDateString(i18n.language)
    }

    const formatEntityValue = (value: unknown): string => {
        if (Array.isArray(value)) {
            return value.map(formatEntityValue).filter(Boolean).join(', ')
        }

        if (!value || typeof value !== 'object') {
            return stringifyValue(value)
        }

        const objectValue = value as EntityInterface
        const translatedLabel = translate(objectValue, 'label') ?? translate(objectValue, 'name')

        if (translatedLabel) {
            return translatedLabel
        }

        if (objectValue.firstName || objectValue.lastName) {
            return `${objectValue.firstName ?? ''} ${objectValue.lastName ?? ''}`.trim()
        }

        return stringifyValue(objectValue.name ?? objectValue.label ?? objectValue.title ?? objectValue.value ?? objectValue.id ?? objectValue)
    }

    const formatAddressValue = (value: unknown): string => {
        if (!value || typeof value !== 'object') {
            return stringifyValue(value)
        }

        const address = value as EntityInterface

        if (address.stringAddress || address.address) {
            return stringifyValue(address.stringAddress ?? address.address)
        }

        const rows = [
            address.name ? `${address.name}:` : undefined,
            address.row1,
            address.row2,
            [address.postalCode, address.city].filter(Boolean).join(' '),
            address.country,
        ].filter((row) => typeof row === 'string' && row.trim() !== '')

        return rows.length ? rows.join(', ') : stringifyValue(value)
    }

    const formatCombinedValue = (value: unknown): string => {
        if (Array.isArray(value)) {
            return value.map(formatCombinedValue).filter(Boolean).join(', ')
        }

        if (!value || typeof value !== 'object') {
            return stringifyValue(value)
        }

        return Object.entries(value as EntityInterface)
            .filter(([, fieldValue]) => !isEmptyPropertyData(fieldValue))
            .map(([fieldName, fieldValue]) => {
                if (
                    fieldValue
                    && typeof fieldValue === 'object'
                    && ('data' in fieldValue || 'value' in fieldValue)
                ) {
                    const nestedPropertyValue = fieldValue as PlatformUserPropertyValue
                    return `${getPropertyName(nestedPropertyValue)}: ${stringifyValue(getPropertyData(nestedPropertyValue))}`
                }

                return `${fieldName}: ${stringifyValue(fieldValue)}`
            })
            .join(', ')
    }

    const formatTypedValue = (data: unknown, property?: Property): string|undefined => {
        const propertyType = property?.type as string|undefined

        if (propertyType === 'address') {
            return formatAddressValue(data)
        }

        if (propertyType === 'choice' || propertyType === 'status' || propertyType === 'collection') {
            return formatEntityValue(data)
        }

        if (propertyType === 'date') {
            return formatDateValue(data)
        }

        if (propertyType === 'checkbox') {
            return stringifyValue(data)
        }

        if (
            (propertyType === 'price' || propertyType === 'calculate')
            && data
            && typeof data === 'object'
            && 'amount' in data
        ) {
            return formatCurrencyValue(data as EntityInterface)
        }

        if (propertyType === 'combined') {
            return formatCombinedValue(data)
        }

        return undefined
    }

    const renderPropertyValue = (propertyValue: PlatformUserPropertyValue): ReactNode => {
        const property = getProperty(propertyValue)
        const data = getPropertyData(propertyValue)

        if (isEmptyPropertyData(data)) {
            return "-"
        }

        const typedValue = formatTypedValue(data, property)

        if (typedValue !== undefined) {
            return typedValue
        }

        if (property?.slug) {
            try {
                const renderedValue = getValue({[property.slug]: data} as EntityInterface, property)
                if (renderedValue) {
                    return renderedValue
                }
            } catch (error) {
            }
        }

        return stringifyValue(data)
    }

    const getPropertyName = (propertyValue: PlatformUserPropertyValue): string => {
        const property = getProperty(propertyValue)

        if (!property) {
            return propertyValue.label ?? propertyValue.name ?? propertyValue.key ?? t('app.property')
        }

        return translate(property, 'label') ?? property.name ?? property.slug ?? `${t('app.property')} #${property.id}`
    }

    const contactPropertyValues = getContactPropertyValues(user)

    const customActions: CustomAction[] = [
        {
            method: showUserDetails,
            i18nLabel: 'app.view_details',
            icon: 'eye',
            variant: 'secondary',
        },
        {
            method: handleEnableUser,
            granted: 'enable',
            i18nLabel: 'app.enable_user',
            variant: 'success',
        },
        {
            method: handleDisableUser,
            granted: 'disable',
            i18nLabel: 'app.disable_user',
            variant: 'danger',
        },
    ]

    return <>
        <Listing
            path={`/platform-setting/${platform?.id ?? 1}/user`}
            search={search}
            setSearch={setSearch}
            setting={setting}
            selectable={true}
            toggleReload={toggleReload}
            setToggleReload={setToggleReload}
            editAction={editUser}
            deleteAction={deleteUser}
            customActions={customActions}
        />
        <FormEmptyOffCanvas
            i18nTitle={user.id ? 'app.edit_platform_user' : 'app.add_platform_user'}
            show={show}
            handleClose={handleClose}
            onSuccess={onSuccess}
            path={`/platform-setting/${platform?.id ?? 1}/user/${user?.id ?? 'new'}`}
            formPath={!!user.id}
            method={user.id ? 'patch' : 'post'}
            onExited={onExited}
        />
        <DeleteModal
            handleClose={handleDeleteClose}
            show={showDelete}
            path={`/platform-setting/${platform?.id ?? 1}/user/${user?.id ?? 'error'}`}
            name={'#' + (user ? `${user.name}` : 'error')}
            archive={true}
        />
        <Modal show={showDetails} onHide={handleDetailsClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('app.platform_user_details')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <dl className="row mb-4">
                    <dt className="col-sm-3">{t('app.email')}</dt>
                    <dd className="col-sm-9">{user.contact?.email || user.email || '-'}</dd>
                    <dt className="col-sm-3">{t('app.first_name')}</dt>
                    <dd className="col-sm-9">{user.contact?.firstName || '-'}</dd>
                    <dt className="col-sm-3">{t('app.last_name')}</dt>
                    <dd className="col-sm-9">{user.contact?.lastName || '-'}</dd>
                    {contactPropertyValues.map((propertyValue, index) => (
                        <Fragment key={propertyValue.id ?? index}>
                            <dt className="col-sm-3">{getPropertyName(propertyValue)}</dt>
                            <dd className="col-sm-9">{renderPropertyValue(propertyValue)}</dd>
                        </Fragment>
                    ))}
                </dl>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleDetailsClose}>
                    {t('app.close')}
                </Button>
                {user.enabled ? (
                    <LoadingButton
                        isLoading={isActivationLoading(user, 'disable')}
                        variant="danger"
                        onClick={() => handleDisableUser(user)}
                    >
                        {t('app.disable_user')}
                    </LoadingButton>
                ) : (
                    <LoadingButton
                        isLoading={isActivationLoading(user, 'enable')}
                        variant="success"
                        onClick={() => handleEnableUser(user)}
                    >
                        {t('app.enable_user')}
                    </LoadingButton>
                )}
            </Modal.Footer>
        </Modal>
    </>
}

export default PlatformUserPage
